import axios from 'axios';
import pathfinder from '@fiverr-private/pathfinder';
import ParamsValidationError from '../../errors/ParamsValidationError';
import ChannelSubscriptionError from '../../errors/ChannelSubscriptionError';

const CHANNEL_NAME_MAX_LENGTH = 164;
const SIGNATURE_SUFFIX_LENGTH = 41;
const VALID_CHANNEL_NAME_REGEX = /^[\w_\-=@,.;]*$/g;
export const CHANNEL_NAME_MAX_LENGTH_EXIDED_ERROR_PREFIX = 'Channel name max length: ';
export const INVALID_CHARACTERS_ERROR_MESSAGE = 'Channel names should only include lower and uppercase letters, numbers and the following punctuation _ - = @ , . ;';
export const INVALID_CHANNEL_NAME_ERROR_PREFIX = 'Invalid channel name: ';

export default class ChannelsManager {
    /**
     * @property {object} pusher - Pusher API connection instance
     * @property {string} channelName - Channel name
     * @property {function} channelClass - Channel type class
     * @return {Promise<PrivateChannel | PresenceChannel>} Promise which return <PrivateChannel | PresenceChannel> in resolve or ChannelSubscriptionError in reject
     */
    static subscribe(pusher, channelName, channelClass) {
        const prefixedName = channelClass.addPrefix([channelName])[0];
        const errorMessage = ChannelsManager.validateChannelName(prefixedName, channelClass);

        if (errorMessage) {
            return Promise.reject(new ParamsValidationError(errorMessage));
        }

        const subscribedChannel = pusher.channel(prefixedName);
        if (subscribedChannel) {
            return Promise.resolve(new channelClass(subscribedChannel));
        }

        if (!ChannelsManager.isAuthorizedChannel(pusher, prefixedName)) {
            return this.postAuthorization(pusher, [prefixedName]).then(() =>
                ChannelsManager.subscribeToChannel(pusher, prefixedName, channelClass)
            );
        }

        return ChannelsManager.subscribeToChannel(pusher, prefixedName, channelClass);
    }

    /**
     * @property {object} pusher - Pusher API connection instance
     * @property {string} channelName - Channel name
     * @property {function} channelClass - Channel type class
     */
    static unsubscribe(pusher, channelName, channelClass) {
        pusher.unsubscribe(channelClass.addPrefix([channelName])[0]);
    }

    static isAuthorizedChannel(pusher, prefixedName) {
        return !!pusher.config.auth.preAuth[prefixedName];
    }

    static subscribeToChannel(pusher, prefixedName, channelClass) {
        return new Promise((resolve, reject) => {
            const channel = pusher.subscribe(prefixedName);

            channel.bind('pusher:subscription_succeeded', () => {
                resolve(new channelClass(channel));
            });

            channel.bind('pusher:subscription_error', (status) => {
                reject(new ChannelSubscriptionError(status));
            });
        });
    }

    /**
     * @property {string} prefixedName - full channel name with a prefix
     * @property {function} channelClass - Channel type class
     * @return {string | undefined} - Error message(s) for channel name validation
     */
    static validateChannelName(prefixedName, channelClass) {
        const errorMessages = [];

        if (prefixedName.length > CHANNEL_NAME_MAX_LENGTH) {
            errorMessages.push(`${CHANNEL_NAME_MAX_LENGTH_EXIDED_ERROR_PREFIX}${ChannelsManager.getRawChannelNameMaxLength(channelClass)}`);
        }

        if (!prefixedName.match(VALID_CHANNEL_NAME_REGEX)) {
            errorMessages.push(INVALID_CHARACTERS_ERROR_MESSAGE);
        }

        if (errorMessages.length) {
            return `${INVALID_CHANNEL_NAME_ERROR_PREFIX}${errorMessages.join(', ')}`;
        }
    }

    /**
     * @property {function} channelClass - Channel type class
     * @returns {number} represent maximum length of channel name
     */
    static getRawChannelNameMaxLength(channelClass) {
        return CHANNEL_NAME_MAX_LENGTH - channelClass.channelPrefix.length - SIGNATURE_SUFFIX_LENGTH;
    }

    /**
     * @property {object} pusher - Pusher API connection instance
     * @property {string[]} prefixedChannelNames - array of prefixed channel names
     * @return {Promise} Promise resolve when finish authorization
     */
    static postAuthorization(pusher, prefixedChannelNames) {
        const data = {
            channel_names: prefixedChannelNames,
            socket_id: pusher.connection.socket_id
        };

        return axios({
            method: 'post',
            url: pathfinder('pushkin_auth_endpoint'),
            data
        }).then((response) => {
            const responseJson = response.data;
            for (const channelName in responseJson) {
                pusher.config.auth.preAuth[channelName] = responseJson[channelName];
            }
        });
    }
}
